import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useFallbackId } from '../../useFallbackId';
import { HtmlFieldset, HtmlFieldsetProps } from '../HtmlFieldset';
import { Description } from '../Description';
import { Input, InputProps } from '../Input';
import { useStatePart } from '../hooks/useStatePart';
import styles from './styles.module.scss';

export type Address = {
    street?: string;
    city?: string;
    postcode?: string;
    country?: string;
};

export type AddressInputProps = {
    id?: string;
    label: string;
    name: string;
    description?: string;
    value: Address | null;
    onChange?: (newAddress: Address | null) => void;
    streetInputProps?: Partial<Omit<InputProps, 'value' | 'onChange'>>;
    cityInputProps?: Partial<Omit<InputProps, 'value' | 'onChange'>>;
    postcodeInputProps?: Partial<Omit<InputProps, 'value' | 'onChange'>>;
    countryInputProps?: Partial<Omit<InputProps, 'value' | 'onChange'>>;
} & Partial<Omit<HtmlFieldsetProps, 'label' | 'value' | 'onChange'>>;

export const AddressInput: FunctionComponent<AddressInputProps> = ({
    id,
    description,
    name,
    label,
    value,
    onChange = () => {},
    streetInputProps,
    cityInputProps,
    postcodeInputProps,
    countryInputProps,
    ...fieldsetProps
}) => {
    const inputId = useFallbackId(id);

    const parsedValue = useMemo((): Address => {
        if (value === null) {
            return {};
        }
        return value;
    }, [value]);

    const [street, setStreet] = useStatePart(parsedValue, onChange, 'street');
    const [city, setCity] = useStatePart(parsedValue, onChange, 'city');
    const [postcode, setPostcode] = useStatePart(parsedValue, onChange, 'postcode');
    const [country, setCountry] = useStatePart(parsedValue, onChange, 'country');

    const onStreetChange = useCallback(
        (value: string | null) => {
            setStreet(value || undefined);
        },
        [setStreet]
    );

    const onCityChange = useCallback(
        (value: string | null) => {
            setCity(value || undefined);
        },
        [setCity]
    );

    const onPostcodeChange = useCallback(
        (value: string | null) => {
            setPostcode(value || undefined);
        },
        [setPostcode]
    );

    const onCountryChange = useCallback(
        (value: string | null) => {
            setCountry(value || undefined);
        },
        [setCountry]
    );

    return (
        <HtmlFieldset label={label} className={styles.container} {...fieldsetProps}>
            <Description description={description} className={styles.description} />
            <div className={styles.fieldContainer}>
                <Input
                    id={`${inputId}_street`}
                    name="street"
                    label="Street"
                    value={street || ''}
                    onChange={onStreetChange}
                    autoComplete="address-line1"
                    autoCapitalize="on"
                    containerClassName={styles.streetInput}
                    {...streetInputProps}
                />
                <Input
                    id={`${inputId}_city`}
                    name="city"
                    label="City"
                    value={city || ''}
                    onChange={onCityChange}
                    autoComplete="address-level2"
                    autoCapitalize="on"
                    containerClassName={styles.cityInput}
                    {...cityInputProps}
                />
                <Input
                    id={`${inputId}_postcode`}
                    name="postcode"
                    label="Postcode"
                    value={postcode || ''}
                    onChange={onPostcodeChange}
                    autoComplete="postal-code"
                    containerClassName={styles.postcodeInput}
                    {...postcodeInputProps}
                />
                <Input
                    id={`${inputId}_country`}
                    name="country"
                    label="Country"
                    value={country || ''}
                    onChange={onCountryChange}
                    autoComplete="country-name"
                    autoCapitalize="on"
                    containerClassName={styles.countryInput}
                    {...countryInputProps}
                />
            </div>
        </HtmlFieldset>
    );
};
